import ApiClient from '@/api/ApiClient';
import BaseService from './BaseService';
import Student from '@/api/models/Student';
import Material from '@/api/models/Material';
import MaterialDictionary from '@/dictionary/MaterialDictionary';

class StudentService extends BaseService {
    #prefixUrl = 'api/students/';

    constructor() {
        super();
        this.api = new ApiClient(this.#prefixUrl);
    }

    /**
     * Get all testimonies
     * @returns {Array}
     */
    async get() {
        let students = [];

        const response = await this.api.executeRequest(`/`, {}, this.api.methods.GET);

        if (response.success() && response.getContent() != null) {
            for (let studentData of response.getContent()) {
                students.push(new Student(studentData));
            }
        }

        return students;
    }

    /**
     * Saves student by creating or updating
     * @param {Object} studentData
     * @returns {api/models/Response}
     */
    async save(studentData = {}) {
        const response = await this.api.executeRequest('/', studentData, this.api.methods.POST);

        if (response.success() && response.getContent() != null) {
            const studentObject = new Student(response.getContent());
            response.setContent(studentObject);
        }
        
        return response;
    }

    /**
     * Delets Student
     * @param {integer} studentId
     * @returns {Response} 
     */
    async delete(studentId = null) {
        return await this.api.executeRequest(`/${studentId}`, {}, this.api.methods.DELETE);
    }

    /**
     * Request access to Student
     * @param {integer} id
     * @param {String} email
     * @param {Boolean} performVinculation
     * @returns {Response}
     */
    async grantAccessToStudent(id, email = '', performVinculation = false) {
        const response = await this.api.executeRequest(`/grant_access/${id}`, {
            email: email, 
            performVinculation: (performVinculation ? true : false)
        }, this.api.methods.POST);

        if (response.success() && response.getContent() != null) {
            if (response.getContent().TYPE != undefined) {
                return response;
            }

            const studentObject = new Student(response.getContent());
            response.setContent(studentObject);
        }
        
        return response;
    }

    /**
     * Generates a new Student access password to be sent to Student email
     * @param {Integer} id
     */
    async resetAccessPassword(id = null) {
        return await this.api.executeRequest(`/resend_access/${id}`, {}, this.api.methods.POST);
    }

    /**
     * Removes access to Student User access
     * @param {Integer} id
     */
    async revokeAccess(id = null) {
        return await this.api.executeRequest(`/revoke_access/${id}`, {}, this.api.methods.POST);
    }

    /**
     * Gets Student current Materials
     * @returns {Array}
     */
    async getMaterials(studentId = null) {
        let studentMaterials = [];

        const response = await this.api.executeRequest(`/materials/${studentId}`, {}, this.api.methods.GET);

        if (response.success() && response.getContent() != null) {
            for (let materialData of response.getContent()) {
                studentMaterials.push(new Material(materialData));
            }
        }

        return studentMaterials;
    }

    /**
     * Gets Student current Materials Groups
     * @returns {Array}
     */
    async getMaterialsGroup(studentId = null) {
        const materialDictionary = new MaterialDictionary();

        let studentMaterials = [];

        const response = await this.api.executeRequest(`/materials_group/${studentId}`, {}, this.api.methods.GET);

        if (response.success() && response.getContent() != null) {
            for (let groupValue of response.getContent()) {
                studentMaterials.push(materialDictionary.getItem(groupValue));
            }
        }

        return studentMaterials;
    }

    /**
     * Updates Student allowed Materials
     * @param {Integer} studentId
     * @param {Array} allowedMaterials
     * @returns {Response}
     */
    async syncMaterialsPermissions(studentId = null, allowedMaterials = []) {
        return await this.api.executeRequest(`/material_permissions/${studentId}`, {materials: allowedMaterials}, this.api.methods.POST);
    }
}

export default new StudentService();
